// New Project launcher modal (PR #151, 2026-05-26). Operator-locked:
// "'New Project' should be an option on the dashboard page for sales
// reps - so a rep can add one immediately after logging in. Once
// added, they need to either add a client or find a client in the
// system for which to add a project to."
//
// Flow:
//   1. Pick a mode — "Existing client" (search /clients) or
//      "New client" (type name + optional contact).
//   2. Type the project name + in-hands date (no default — the rep picks it).
//   3. Submit. If new client: POST /clients first, then POST /projects
//      with the resolved client_id.
//   4. Parent receives the new project id and navigates.
//
// Contacts on the order (Stephanie 2026-10-12): the rep picks which of the
// account's contacts are on this order while creating it (at least one is
// required). A new account, or one with no contacts yet, adds a first contact
// right here. Saved as the project's main contact + its order contacts, which
// "Send proof to client" uses.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ContactLocationSelect, emptyLocation, locationPayload, useClientLocations } from "@/components/contacts/ContactLocationSelect";
import { LeadSourceField, leadSourceBody, leadSourceComplete, useLeadSourceReady, type LeadSourceValue } from "@/components/clients/LeadSourceField";

interface ClientListRow {
  id: string;
  name: string;
}

interface ClientListResponse {
  rows: ClientListRow[];
}

interface ContactRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  isPrimary: boolean | null;
}

export interface NewProjectResult {
  projectId: string;
  clientId: string;
  clientName: string;
}

interface Props {
  onCancel: () => void;
  onCreated: (result: NewProjectResult) => void;
}

type Mode = "existing" | "new";


export function NewProjectLauncherModal(props: Props) {
  const { onCancel, onCreated } = props;

  const [mode, setMode] = useState<Mode>("existing");
  const [selectedClient, setSelectedClient] = useState<ClientListRow | null>(
    null,
  );
  const [clientSearch, setClientSearch] = useState("");
  const [newClientName, setNewClientName] = useState("");
  // A new account says how it found us (CEO 2026-10-21).
  const [lead, setLead] = useState<LeadSourceValue>({ source: "", detail: "" });
  const leadReady = useLeadSourceReady(); // asked only once the DB step has run (S1 review F2)
  const [projectName, setProjectName] = useState("");
  // No default — the rep picks the in-hands date (Stephanie 2026-11-02: it
  // defaulted to a month out; "the sales rep needs to manually choose").
  const [inHandsDate, setInHandsDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [newContact, setNewContact] = useState({ first: "", last: "", email: "", phone: "" });
  const [error, setError] = useState<string | null>(null);
  // A new contact on a main account with locations (Bozzuto) is assigned to
  // one, and the opportunity goes on that location (Stephanie 2026-10-20).
  const locationsQ = useClientLocations(mode === "existing" ? selectedClient?.id : null);
  const company = locationsQ.data?.isMain ? locationsQ.data.company : null;
  const [location, setLocation] = useState(emptyLocation);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, onCancel]);

  // Debounced search of existing clients.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(clientSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const clientsQ = useQuery({
    queryKey: ["new-project-launcher", "clients", debouncedSearch],
    queryFn: () =>
      api<ClientListResponse>(
        `/clients?search=${encodeURIComponent(debouncedSearch)}&limit=20`,
      ),
    enabled: mode === "existing" && debouncedSearch.length > 0,
  });

  const contactsQ = useQuery({
    queryKey: ["new-project-launcher", "contacts", selectedClient?.id],
    queryFn: () => api<{ contacts: ContactRow[] }>(`/clients/${selectedClient!.id}/contacts`),
    enabled: mode === "existing" && selectedClient !== null,
  });
  const accountContacts = mode === "existing" ? contactsQ.data?.contacts ?? [] : [];
  // The account's primary contact starts ticked.
  useEffect(() => {
    const primary = contactsQ.data?.contacts.find((c) => c.isPrimary) ?? (contactsQ.data?.contacts.length === 1 ? contactsQ.data.contacts[0] : undefined);
    setPicked(primary ? new Set([primary.id]) : new Set());
  }, [contactsQ.data]);
  // A new account, or an account with no contacts yet, adds its first contact here.
  const needsNewContact = mode === "new" || (selectedClient !== null && contactsQ.isSuccess && accountContacts.length === 0);
  const newContactOk =
    newContact.first.trim().length > 0 && newContact.last.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newContact.email.trim());
  const contactsOk = needsNewContact ? newContactOk : picked.size > 0;
  const locationCheck = needsNewContact ? locationPayload(company, location) : ({ ok: true, body: {} } as const);

  const submit = async (): Promise<void> => {
    setError(null);
    const trimmedProject = projectName.trim();
    if (trimmedProject.length === 0) {
      setError("Opportunity name is required.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(inHandsDate)) {
      setError("Pick the in-hands date.");
      return;
    }

    let clientId: string | null = null;
    let clientName = "";
    if (mode === "existing") {
      if (!selectedClient) {
        setError("Pick an existing account or switch to 'New account'.");
        return;
      }
      clientId = selectedClient.id;
      clientName = selectedClient.name;
    } else {
      const trimmedClient = newClientName.trim();
      if (trimmedClient.length === 0) {
        setError("Account name is required.");
        return;
      }
      if (!leadSourceComplete(lead, leadReady)) {
        setError(lead.source === "other" ? "Add a short note when the source is Other." : "Pick how this client found us.");
        return;
      }
      clientName = trimmedClient;
    }

    if (!contactsOk) {
      setError(needsNewContact ? "Add the contact's first name, last name and email." : "Tick at least one contact on this order.");
      return;
    }
    if (!locationCheck.ok) {
      setError(locationCheck.error);
      return;
    }

    // A new account carries how it found us (CEO 2026-10-21).
    const newClientBody = {
      name: clientName,
      ...leadSourceBody(lead, leadReady),
    };
    setSubmitting(true);
    try {
      if (mode === "new") {
        const res = await api<{ client: { id: string; name: string } }>(
          "/clients",
          {
            method: "POST",
            body: JSON.stringify(newClientBody),
          },
        );
        clientId = res.client.id;
      }
      if (!clientId) throw new Error("client resolution failed");

      let contactIds = [...picked];
      if (needsNewContact) {
        const c = await api<{ contact: { id: string; clientId?: string } }>(`/clients/${clientId}/contacts`, {
          method: "POST",
          body: JSON.stringify({
            ...locationCheck.body,
            first_name: newContact.first.trim(),
            last_name: newContact.last.trim(),
            email: newContact.email.trim(),
            // Optional phone (Stephanie 2026-10-14).
            ...(newContact.phone.trim() ? { phone: newContact.phone.trim() } : {}),
            is_primary: true,
          }),
        });
        contactIds = [c.contact.id];
        // Assigned to a location → the opportunity belongs to that location.
        if (c.contact.clientId) clientId = c.contact.clientId;
      }

      const proj = await api<{ project: { id: string } }>("/projects", {
        method: "POST",
        body: JSON.stringify({
          client_id: clientId,
          name: trimmedProject,
          in_hands_date: inHandsDate,
          primary_contact_id: contactIds[0] ?? null,
        }),
      });
      // Save who's on the order (ignored until the order contacts table is on).
      await api(`/projects/${proj.project.id}/order-contacts`, {
        method: "PUT",
        body: JSON.stringify({ contact_ids: contactIds }),
      }).catch(() => undefined);

      onCreated({
        projectId: proj.project.id,
        clientId,
        clientName,
      });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      const msg =
        err instanceof ApiError && err.status === 403
          ? "Only client reps and admins can add a new contact. Ask one to add it, then try again."
          : body?.error ?? (err instanceof Error ? err.message : "Could not create the project.");
      setError(msg);
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    projectName.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(inHandsDate) &&
    ((mode === "existing" && selectedClient !== null) ||
      (mode === "new" && newClientName.trim().length > 0 && leadSourceComplete(lead, leadReady))) &&
    contactsOk;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="New opportunity"
    >
      <div className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-brown-50 px-6 py-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-brown-500">
              New opportunity
            </h2>
            <p className="mt-1 text-xs text-brown-400">
              Pick the account this opportunity belongs to, then give it a name.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md p-1 text-brown-300 hover:bg-cream-50 hover:text-brown-500 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setMode("existing");
                setError(null);
              }}
              className={cn(
                "flex-1 rounded-md border px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                mode === "existing"
                  ? "border-ink bg-canvas text-ink"
                  : "border-brown-100 bg-white text-brown-400 hover:border-brown-200",
              )}
            >
              Existing account
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("new");
                setError(null);
              }}
              className={cn(
                "flex-1 rounded-md border px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                mode === "new"
                  ? "border-ink bg-canvas text-ink"
                  : "border-brown-100 bg-white text-brown-400 hover:border-brown-200",
              )}
            >
              New account
            </button>
          </div>

          {/* Client section */}
          {mode === "existing" ? (
            <div className="space-y-2">
              <label className="block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                  Find an account
                </span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brown-300" />
                  <Input
                    autoFocus
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setSelectedClient(null);
                    }}
                    placeholder="Start typing the account name…"
                    className="pl-9"
                  />
                </div>
              </label>
              {selectedClient && (
                <p className="rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-brown-500">
                  <span className="font-semibold">Selected:</span>{" "}
                  {selectedClient.name}
                </p>
              )}
              {!selectedClient &&
                debouncedSearch.length > 0 &&
                clientsQ.data && (
                  <ul className="max-h-48 overflow-y-auto rounded-md border border-brown-100 divide-y divide-brown-50">
                    {clientsQ.data.rows.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-brown-300">
                        No matches. Try a different name or switch to "New
                        account".
                      </li>
                    ) : (
                      clientsQ.data.rows.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedClient(c);
                              setClientSearch(c.name);
                            }}
                            className="flex w-full items-center justify-between px-3 py-2 text-sm text-brown-500 hover:bg-cream-50"
                          >
                            <span>{c.name}</span>
                            <Check className="h-3.5 w-3.5 text-muted opacity-0 group-hover:opacity-100" />
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
            </div>
          ) : (
            <div>
              <label className="block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                  Account name
                </span>
                <Input
                  autoFocus
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="Acme Co"
                  maxLength={200}
                />
              </label>
              <p className="mt-1 text-xs text-brown-300">
                Addresses and more contacts can be added from the account page
                after creation.
              </p>
              {leadReady && <div className="mt-3">
                <LeadSourceField
                  id="launcher-lead-source"
                  value={lead}
                  onChange={setLead}
                  labelClassName="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300"
                />
              </div>}
            </div>
          )}

          {/* Contacts on this order — required (Stephanie 2026-10-12) */}
          {(mode === "new" || selectedClient) && (
            <div className="space-y-2">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                Contacts on this order
              </span>
              {mode === "existing" && contactsQ.isLoading ? (
                <div className="h-10 animate-pulse rounded-md bg-canvas" />
              ) : needsNewContact ? (
                <div className="space-y-2">
                  <p className="text-xs text-brown-400">
                    {mode === "new" ? "Add the first contact for this account." : "This account has no contacts yet — add one."} Proofs go only to the order's contacts.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newContact.first} onChange={(e) => setNewContact({ ...newContact, first: e.target.value })} placeholder="First name" maxLength={120} aria-label="Contact first name" />
                    <Input value={newContact.last} onChange={(e) => setNewContact({ ...newContact, last: e.target.value })} placeholder="Last name" maxLength={120} aria-label="Contact last name" />
                  </div>
                  <Input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" maxLength={254} aria-label="Contact email" />
                  <Input type="tel" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone (optional)" maxLength={64} aria-label="Contact phone" />
                  {company ? <ContactLocationSelect company={company} value={location} onChange={setLocation} labelClassName="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300" /> : null}
                </div>
              ) : (
                <div className="space-y-1 rounded-md border border-brown-100 p-2">
                  {accountContacts.map((c) => (
                    <label key={c.id} className="flex min-h-[36px] items-center gap-3 px-1 text-sm text-brown-500">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-ink"
                        checked={picked.has(c.id)}
                        onChange={() => {
                          const next = new Set(picked);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          setPicked(next);
                        }}
                      />
                      <span>
                        {c.firstName} {c.lastName}
                        <span className="block text-xs text-brown-300">{c.email ?? "no email on file"}</span>
                      </span>
                    </label>
                  ))}
                  <p className="px-1 pt-1 text-xs text-brown-300">Tick everyone on this order — proofs go only to them. At least one is required.</p>
                </div>
              )}
            </div>
          )}

          {/* Project section */}
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                Opportunity name
              </span>
              <Input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. 2026 employee swag refresh"
                maxLength={200}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                In-hands date
              </span>
              <Input
                type="date"
                value={inHandsDate}
                onChange={(e) => setInHandsDate(e.target.value)}
              />
            </label>
          </div>
        </div>

        {error && (
          <p className="px-6 pb-2 text-sm text-rust-600">{error}</p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-brown-50 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create opportunity"}
          </Button>
        </div>
      </div>
    </div>
  );
}
